/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();

import { useTranslations } from 'next-intl'
import React, { useMemo } from 'react'
import { Competency } from '@/components/quality-explorer/types'
import { useRouter } from '@/i18n/navigation'

/** A description of available properties for this component. */
type CompetencySelectProps = {
  /** The list of competencies to display. */
  competencies: Array<Competency>

  /** The slug of the selected competency. */
  selected: string | undefined

  /** Whether the component is in a loading state. */
  isLoading: boolean
}

/**
 * A basic select component for choosing a competency. The competencies are grouped by category.
 */
const CompetencySelect: React.FC<CompetencySelectProps> = ({ competencies, selected, isLoading }) => {
  const t = useTranslations('CompetencySelect')
  const router = useRouter()

  /** Memo-ise the competencies to be ordered by the category they're in. */
  const competencyGroups = useMemo(() => {
    if (!competencies) return {}
    return competencies.reduce(
      (acc, comp) => {
        if (!acc[comp.category]) {
          acc[comp.category] = []
        }
        acc[comp.category].push(comp)
        return acc
      },
      {} as Record<string, Array<Competency>>,
    )
  }, [competencies])

  return (
    <div
      className={'flex flex-col gap-2 max-w-(--breakpoint-sm) mx-auto bg-center bg-no-repeat'}
      style={{
        backgroundImage: 'url(/images/qe-select-bg.png)',
        backgroundSize: '70%',
      }}
    >
      <div className={'flex flex-col gap-4 mx-auto py-10 w-full max-w-md px-4'}>
        <label className={'text-primary-500 text-xl text-center'} htmlFor={'competency-selector'}>
          {t('selectQualityLabel')}
        </label>
        <div className={'relative w-full'}>
          <select
            id={'competency-selector'}
            className={
              'w-full py-4 pl-6 pr-12 border-2 border-primary-500 rounded-full bg-white text-primary-500 font-medium appearance-none truncate'
            }
            value={selected || ''}
            onChange={(e) => router.push(`/qualities-explorer/${e.target.value}`)}
            disabled={isLoading}
          >
            <option value={''}>Select...</option>
            {Object.entries(competencyGroups).map(([category, comps]) => (
              <optgroup key={category} label={category}>
                {comps.map((comp) => (
                  <option key={comp.id} value={comp.slug}>
                    {comp.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <div className={'pointer-events-none absolute inset-y-0 right-6 flex items-center'}>
            <svg className={'h-4 w-4 text-primary-500'} fill={'none'} stroke={'currentColor'} viewBox={'0 0 24 24'}>
              <path strokeLinecap={'round'} strokeLinejoin={'round'} strokeWidth={'2'} d={'M19 9l-7 7-7-7'} />
            </svg>
          </div>
        </div>
      </div>
    </div>
  )
}

export { CompetencySelect }
